import { WSTableCell, WSTableColumn } from "@wingspanhq/fe-component-library";
import {
  PayeeRow,
  PayeeRowTaxInformationShare,
  SearchRowEngagementStatusRollup,
  SearchRowRequirementsStatusRollup,
  SearchRowTinStatus
} from "@wingspanhq/wingspan-api-sdk";
import React from "react";
import { getCountryName } from "../../../../query/users/selectors";
import { selectorSearchRowName } from "../../../../shared/selectors/selectorSearchRowName";

export const getColumns = (
  isOrganizationContext: boolean
): WSTableColumn<PayeeRow>[] => [
  {
    config: {
      gridTemplateSizeMin: "0",
      gridTemplateSizeMax: "1.5fr",
      header: { text: "Name" }
    },
    renderFunction: rowItem => {
      const name = selectorSearchRowName(rowItem.data) || "--";
      return (
        <WSTableCell
          avatar={{ type: "text", text: name }}
          text={name}
          secondaryText={rowItem.data.profile?.doingBusinessAs ?? undefined}
        />
      );
    }
  },
  {
    config: {
      gridTemplateSizeMin: "0",
      gridTemplateSizeMax: "1fr",
      header: { text: isOrganizationContext ? "Organization" : "Contact" },
      hideOnScreens: ["XS"]
    },
    renderFunction: rowItem => (
      <WSTableCell text={rowItem.data.email ?? undefined} />
    )
  },
  {
    config: {
      gridTemplateSizeMin: "0",
      gridTemplateSizeMax: "1fr",
      header: { text: "Tax ID" }
    },
    renderFunction: rowItem => <TaxIdCell data={rowItem.data} />
  },
  {
    config: {
      gridTemplateSizeMin: "0",
      gridTemplateSizeMax: "1fr",
      header: { text: "Location" },
      hideOnScreens: ["XS"]
    },
    renderFunction: rowItem => (
      <WSTableCell
        text={rowItem.data.location?.city ?? undefined}
        secondaryText={
          rowItem.data.location?.country
            ? getCountryName(rowItem.data.location.country)
            : undefined
        }
      />
    )
  },
  {
    config: {
      gridTemplateSizeMin: "0",
      gridTemplateSizeMax: "1fr",
      header: { text: "Requirements" }
    },
    renderFunction: rowItem => <RequirementCell data={rowItem.data} />
  }
];

export const TaxIdCell: React.FC<{ data: PayeeRow }> = props => {
  // `taxInformationShare` tells us who supplied the tax info; the search row never
  // materializes the SSN/EIN last-four (PII), so the pill shows status only.
  const secondaryText =
    props.data.taxInformationShare === PayeeRowTaxInformationShare.ALLOW
      ? "Contractor shared"
      : "Provided by you";

  switch (props.data.tinStatus) {
    case SearchRowTinStatus.PENDING:
      return (
        <WSTableCell
          pill={{ icon: "time", theme: "neutral", text: "Pending" }}
          secondaryText={secondaryText}
        />
      );
    case SearchRowTinStatus.VERIFIED:
      return (
        <WSTableCell
          pill={{ icon: true, theme: "success", text: "Matched" }}
          secondaryText={secondaryText}
        />
      );
    case SearchRowTinStatus.FAILED:
      return (
        <WSTableCell
          pill={{ icon: true, theme: "warning", text: "Mismatch" }}
          secondaryText={secondaryText}
        />
      );
    default:
      return <WSTableCell text="--" />;
  }
};

export const RequirementCell: React.FC<{ data: PayeeRow }> = props => {
  const isNotSignedUp =
    props.data.engagementStatus ===
    SearchRowEngagementStatusRollup.NOT_SIGNED_UP;

  if (
    props.data.engagementStatus === SearchRowEngagementStatusRollup.INACTIVE
  ) {
    return (
      <WSTableCell
        pill={{ icon: "archive", theme: "neutral", text: "Archived" }}
      />
    );
  }

  if (
    props.data.requirementsStatus === SearchRowRequirementsStatusRollup.COMPLETE
  ) {
    return (
      <WSTableCell
        pill={{ icon: true, theme: "success", text: "Complete" }}
        secondaryText={
          isNotSignedUp ? "Not signed up" : "All requirements complete"
        }
      />
    );
  }

  if (
    props.data.requirementsStatus ===
    SearchRowRequirementsStatusRollup.INCOMPLETE
  ) {
    return (
      <WSTableCell
        pill={{ icon: true, theme: "warning", text: "Incomplete" }}
        secondaryText={
          isNotSignedUp
            ? "Not yet signed up"
            : "Awaiting requirements completion"
        }
        secondaryTextColor="amber400"
      />
    );
  }

  return <WSTableCell text="--" />;
};
